import { createContext, useContext, useState, type FormEvent, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Award,
  CalendarDays,
  ChevronLeft,
  CircleEllipsis,
  LayoutDashboard,
  Map,
  PlayCircle,
  Plus,
  Search,
  Settings,
  Trophy,
  X,
} from "lucide-react";
import { Link, NavLink, useLocation, useNavigate } from "../lib/router";
import { apiRequest } from "../lib/api";
import { useResource } from "../lib/queries";
import { demoCatalog } from "../data/demo";
import type { Catalog, ReplayDetail } from "../types";

const desktopNav = [
  { label: "Dashboard", to: "/", icon: LayoutDashboard },
  { label: "Replays", to: "/replays", icon: PlayCircle },
  { label: "Seasons", to: "/seasons", icon: CalendarDays },
  { label: "Achievements", to: "/achievements", icon: Award },
  { label: "Map Career", to: "/map-career", icon: Map },
  { label: "Hall of Fame", to: "/hall-of-fame", icon: Trophy },
  { label: "Settings", to: "/settings", icon: Settings },
];

const mobileNav = [
  { label: "Dashboard", to: "/", icon: LayoutDashboard },
  { label: "Replays", to: "/replays", icon: PlayCircle },
  { label: "Seasons", to: "/seasons", icon: CalendarDays },
  { label: "Achievements", to: "/achievements", icon: Award },
  { label: "More", to: "/settings", icon: CircleEllipsis },
];

export function AppShell({ children }: { children: ReactNode }) {
  const [addOpen, setAddOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const query = search.trim();
    setSearchOpen(false);
    navigate(query ? `/replays?search=${encodeURIComponent(query)}` : "/replays");
  };

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <Link className="brand" to="/" aria-label="TDS Coach Career dashboard">
          <img src="/brand-mark.png" alt="" />
          <span>
            TDS Coach
            <small>Career</small>
          </span>
        </Link>
        <nav aria-label="Primary navigation">
          {desktopNav.map(({ label, to, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              className={({ isActive }) => (isActive ? "active" : "")}
            >
              <Icon />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-season">
          <div>
            <span>Season 4</span>
            <b>S4</b>
          </div>
          <small>Career level</small>
          <strong>47</strong>
          <div className="sidebar-season__bar">
            <span />
          </div>
          <small>4,620 / 7,500 XP</small>
        </div>
        <button className="sidebar-collapse" type="button" aria-label="Collapse sidebar">
          <ChevronLeft />
        </button>
      </aside>

      <main className="main-content">
        <div className="mobile-appbar">
          <Link to="/" className="mobile-brand" aria-label="Dashboard">
            <img src="/brand-mark.png" alt="" />
          </Link>
          <span>
            {desktopNav.find((item) =>
              item.to === "/"
                ? location.pathname === "/"
                : location.pathname.startsWith(item.to),
            )?.label ?? "TDS Coach Career"}
          </span>
          <div>
            <button type="button" onClick={() => setSearchOpen(true)} aria-label="Search">
              <Search />
            </button>
            <button className="button button--icon button--gold" type="button" onClick={() => setAddOpen(true)} aria-label="Add replay">
              <Plus />
            </button>
          </div>
        </div>
        <ShellActionsContext.Provider value={{ openAddReplay: () => setAddOpen(true), openSearch: () => setSearchOpen(true) }}>
          {children}
        </ShellActionsContext.Provider>
      </main>

      <nav className="mobile-nav" aria-label="Mobile navigation">
        {mobileNav.map(({ label, to, icon: Icon }) => (
          <NavLink key={to} to={to} end={to === "/"}>
            <Icon />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>

      {addOpen ? <AddReplayDialog onClose={() => setAddOpen(false)} /> : null}
      {searchOpen ? (
        <div className="dialog-backdrop" role="presentation" onMouseDown={() => setSearchOpen(false)}>
          <form className="search-dialog" onSubmit={submitSearch} onMouseDown={(event) => event.stopPropagation()}>
            <Search />
            <input
              autoFocus
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search replays, maps, notes, or reviews…"
              aria-label="Search career"
            />
            <button type="button" onClick={() => setSearchOpen(false)} aria-label="Close search">
              <X />
            </button>
          </form>
        </div>
      ) : null}
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  eyebrow,
  actions = true,
  children,
}: {
  title: string;
  subtitle?: string;
  eyebrow?: string;
  actions?: boolean;
  children?: ReactNode;
}) {
  const outlet = useShellActions();
  return (
    <header className="page-header">
      <div>
        {eyebrow ? <div className="page-header__eyebrow">{eyebrow}</div> : null}
        <h1>{title}</h1>
        {subtitle ? <p>{subtitle}</p> : null}
      </div>
      <div className="page-header__actions">
        {children}
        {actions ? (
          <>
            <button className="button button--icon button--quiet desktop-only" type="button" onClick={outlet.openSearch} aria-label="Search career">
              <Search />
            </button>
            <button className="button button--gold desktop-only" type="button" onClick={outlet.openAddReplay}>
              <Plus />
              Add Replay
            </button>
          </>
        ) : null}
      </div>
    </header>
  );
}

type ShellActions = { openAddReplay: () => void; openSearch: () => void };
const ShellActionsContext = createContext<ShellActions | null>(null);

function useShellActions(): ShellActions {
  const actions = useContext(ShellActionsContext);
  if (!actions) throw new Error("PageHeader must be rendered inside AppShell.");
  return actions;
}

function AddReplayDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const catalogQuery = useResource<Catalog>(["catalog"], "/api/catalog", demoCatalog);
  const catalog = catalogQuery.data ?? demoCatalog;
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    mapId: catalog.maps[0]?.id ?? "crossroads",
    modeId: catalog.modes[4]?.id ?? "fallen",
    teamSize: "Solo",
    result: "Win",
    youtubeUrl: "",
    isPromotionTrial: false,
  });

  const mutation = useMutation({
    mutationFn: async () =>
      (
        await apiRequest<ReplayDetail>("/api/replays", {
          method: "POST",
          body: JSON.stringify({
            ...form,
            towerIds: [],
            consumableIds: [],
            commentaryThinking: "",
            commentaryStrategy: "",
            commentaryProblems: "",
            commentaryNoticed: "",
            commentaryQuestions: "",
            status: form.isPromotionTrial ? "Promotion Trial" : "Pending Review",
            isSeasonFinale: false,
            isCareerMilestone: false,
          }),
        })
      ).data,
    onSuccess: (replay) => {
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      void queryClient.invalidateQueries({ queryKey: ["replays"] });
      onClose();
      navigate(`/replays/${replay.id}`);
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    mutation.mutate();
  };

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <form className="dialog" onSubmit={submit} onMouseDown={(event) => event.stopPropagation()}>
        <div className="dialog__header">
          <div>
            <h2>Add Replay</h2>
            <p>Create the replay first; the coach review is added later.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close">
            <X />
          </button>
        </div>
        <div className="form-grid">
          <label>
            Date
            <input type="date" value={form.date} onChange={(event) => setForm((current) => ({ ...current, date: event.target.value }))} required />
          </label>
          <label>
            Map
            <select value={form.mapId} onChange={(event) => setForm((current) => ({ ...current, mapId: event.target.value }))}>
              {catalog.maps.map((map) => <option key={map.id} value={map.id}>{map.name}</option>)}
            </select>
          </label>
          <label>
            Mode
            <select value={form.modeId} onChange={(event) => setForm((current) => ({ ...current, modeId: event.target.value }))}>
              {catalog.modes.map((mode) => <option key={mode.id} value={mode.id}>{mode.name}</option>)}
            </select>
          </label>
          <label>
            Team size
            <select value={form.teamSize} onChange={(event) => setForm((current) => ({ ...current, teamSize: event.target.value }))}>
              {["Solo", "Duo", "Trio", "Squad"].map((size) => <option key={size}>{size}</option>)}
            </select>
          </label>
          <label>
            Result
            <select value={form.result} onChange={(event) => setForm((current) => ({ ...current, result: event.target.value }))}>
              <option>Win</option>
              <option>Loss</option>
            </select>
          </label>
          <label className="form-grid__wide">
            YouTube URL
            <input type="url" value={form.youtubeUrl} onChange={(event) => setForm((current) => ({ ...current, youtubeUrl: event.target.value }))} placeholder="https://youtube.com/watch?v=…" />
          </label>
        </div>
        <label className="check-control">
          <input type="checkbox" checked={form.isPromotionTrial} onChange={(event) => setForm((current) => ({ ...current, isPromotionTrial: event.target.checked }))} />
          <span>This replay is a promotion trial</span>
        </label>
        {mutation.error ? <p className="form-error">{mutation.error.message}</p> : null}
        <div className="dialog__actions">
          <button className="button button--quiet" type="button" onClick={onClose}>Cancel</button>
          <button className="button button--gold" type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? "Creating…" : "Create Replay"}
          </button>
        </div>
      </form>
    </div>
  );
}
